import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useAccount } from './useAccount'
import { API_BASE_URL } from '../constants/system'
import type { PipelineQueue } from '../types/autocoderQueue'

export interface UsePipelineQueueResult {
  data: PipelineQueue | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

/**
 * The per-pipeline coding queue: GET /pipelines/:id/queue.
 *
 * Sits beside `usePipelineThroughput` and polls on the SAME cadence, because
 * the two are read together at the top of the Throughput view and a queue that
 * refreshed on its own clock would show batches the panels below it deny.
 * `enabled` is the mount gate: an unmounted/unselected view must not poll.
 */
export const usePipelineQueue = (
  customer: string,
  job: string,
  pipelineId: string,
  pollSeconds: number,
  enabled = true,
): UsePipelineQueueResult => {
  const account = useAccount()

  const query = useQuery<PipelineQueue, Error>({
    queryKey: ['autocoder-pipeline-queue', customer, job, pipelineId],
    queryFn: async () => {
      const res = await axios.get<PipelineQueue>(
        `${API_BASE_URL}/autocoder/${encodeURIComponent(customer)}/${encodeURIComponent(job)}` +
          `/pipelines/${encodeURIComponent(pipelineId)}/queue`,
        { headers: { Accept: 'application/json' } },
      )
      return res.data
    },
    enabled: enabled && !!customer && !!job && !!pipelineId && !!account.user,
    // The floor exists so a bad/zero pollSeconds cannot spin this into a tight
    // loop; it is NOT a rate limit. It tracks the server's fastest deliberate
    // cadence (activePollSeconds = 2s) so the queue strip can keep step with the
    // panels above it during a fast drain — a 5s floor under a 2s payload meant
    // the strip lagged the numbers it sits next to.
    refetchInterval: Math.max(2, pollSeconds) * 1000,
    refetchIntervalInBackground: false,
    staleTime: 2 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? query.error.message : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

export default usePipelineQueue
